import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ArrowBackIosNewRoundedIcon from "@mui/icons-material/ArrowBackIosNewRounded";
import HomeRoundedIcon from "@mui/icons-material/HomeRounded";
import InventoryRoundedIcon from "@mui/icons-material/InventoryRounded";
import PersonRoundedIcon from "@mui/icons-material/PersonRounded";
import LocalPhoneRoundedIcon from "@mui/icons-material/LocalPhoneRounded";
import CalendarMonthRoundedIcon from "@mui/icons-material/CalendarMonthRounded";
import EventAvailableRoundedIcon from "@mui/icons-material/EventAvailableRounded";
import CategoryRoundedIcon from "@mui/icons-material/CategoryRounded";
import InfoRoundedIcon from "@mui/icons-material/InfoRounded";
import * as XLSX from "xlsx";
import { ReactComponent as ExcelExportIcon } from "../assets/img/icons/excel_export.svg";
import { saveAs } from "file-saver";
import LandscapeRoundedIcon from "@mui/icons-material/LandscapeRounded";
import { SecureImage } from "./SecureImage";

function OrderStatusTable() {
  const location = useLocation();
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { orderStatusData = [], orderStatus = "" } = location.state || {};
  const [orders, setOrders] = useState([]);
  console.log(orders);

  useEffect(() => {
    setOrders(orderStatusData);
  }, [orderStatusData]);

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    const date = new Date(dateString);
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  //   Group tag numbers as per order number
  const groupedOrders = Object.values(
    orders.reduce((acc, order) => {
      const key = order.order_no;

      if (!acc[key]) {
        acc[key] = {
          ...order,
          selectedTags: Array.isArray(order.selectedTags)
            ? [...order.selectedTags]
            : [order.selectedTags],
          selectedTagElements: order.selectedTagElements
            ? [...order.selectedTagElements]
            : [],
          rejectedReasons: order.rejectedReason ? [order.rejectedReason] : [],
        };
      } else {
        // Merge selectedTags
        const newTags = Array.isArray(order.selectedTags)
          ? order.selectedTags
          : [order.selectedTags];

        acc[key].selectedTags.push(...newTags);

        // Merge selectedTagElements
        if (order.selectedTagElements?.length) {
          acc[key].selectedTagElements.push(...order.selectedTagElements);
        }

        // Merge rejectedReasons
        if (
          order.rejectedReason &&
          !acc[key].rejectedReasons.includes(order.rejectedReason)
        ) {
          acc[key].rejectedReasons.push(order.rejectedReason);
        }
      }

      return acc;
    }, {})
  );

  // Excel Export
  const exportGroupedToExcel = () => {
    if (!groupedOrders.length) return;

    // Prepare data for Excel
    const excelData = groupedOrders.map((order) => {
      const baseData = {
        "Order No": order.order_no,
        "Tag Nos": Array.isArray(order.selectedTags)
          ? order.selectedTags.join(", ")
          : order.selectedTags || "-",
        "Customer Name": order.customerName || "-",
        Phone: order.customerPhone || "-",
        "Order Date": formatDate(order.createdAt),
        "Delivery Date": formatDate(order.deliveryDate),
      };

      // Add Reason column only for Reject(DM) status
      if (orderStatus === "Reject(DM)") {
        baseData["Reason"] = order.rejectedReasons?.length
          ? order.rejectedReasons.filter((r) => r).join("; ")
          : "-";
      }

      return baseData;
    });

    // Create worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);

    // Create workbook
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Orders");

    // Generate Excel file
    const excelBuffer = XLSX.write(workbook, {
      bookType: "xlsx",
      type: "array",
    });

    // Save file
    const fileData = new Blob([excelBuffer], {
      type: "application/octet-stream",
    });

    const statusFileNameMap = {
      New: "New",
      "Confirm(DM)": "Confirmed",
      "Reject(DM)": "Rejected",
      "Order(DM)": "Ordered",
      "Accept(WH)": "Accepted",
      "Unavailable(WH)": "Unavailable",
      "Dispatched(SP)": "Dispatched",
    };

    const fileNamePrefix = statusFileNameMap[orderStatus] || "Orders";

    saveAs(
      fileData,
      `${fileNamePrefix}_Orders_${new Date().toISOString().slice(0, 10)}.xlsx`
    );
  };

  return (
    <Box sx={{ bgcolor: "#394A5C", minHeight: "100vh", p: isMobile ? 2 : 6 }}>
      {/* TOP ACTION BAR */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
          mb: 3,
        }}
      >
        {/* BACK BUTTON */}
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate(-1)}
        >
          <ArrowBackIosNewRoundedIcon
            sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#42A996" }}
          />
        </Box>

        {/* HOME BUTTON */}
        <Box
          sx={{
            width: 36,
            height: 36,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 3,
            bgcolor: "#FFF",
            p: 0.5,
            cursor: "pointer",
          }}
          onClick={() => navigate("/admin-dash")}
        >
          <HomeRoundedIcon
            sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#42A996" }}
          />
        </Box>
      </Box>

      {/* HEADER & Excel Export Icon */}
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",

          mb: 3,
        }}
      >
        {/* Heading */}
        <Typography
          sx={{
            color: "#fff",
            fontSize: { xs: 24, sm: 32 },
            fontWeight: 700,
            mb: 1,
          }}
        >
          {orderStatus === "New" && "New Orders"}
          {orderStatus === "Confirm(DM)" && "Confirmed Orders"}
          {orderStatus === "Reject(DM)" && "Rejected Orders"}
          {orderStatus === "Order(DM)" && "Placed Orders"}
          {orderStatus === "Accept(WH)" && "Accepted Orders"}
          {orderStatus === "Unavailable(WH)" && "Unavailable Orders"}
          {orderStatus === "Dispatched(SP)" && "Dispatched Orders"}
          <span style={{ fontSize: 14, marginLeft: 5 }}>
            ({orders.length} {orders.length === 1 ? "order" : "orders"} found)
          </span>
        </Typography>

        {/* Excel Export */}
        <Box>
          <IconButton
            onClick={exportGroupedToExcel}
            sx={{
              bgcolor: "#FFF",
              borderRadius: 2,
              width: 36,
              height: 36,
              "&:hover": {
                bgcolor: "#1c4b42ff",
                "& svg": {
                  fill: "#ffffff", // <-- icon color on hover
                },
              },
              "& svg": {
                transition: "0.2s",
                fill: "#42A996", // <-- default icon color
              },
            }}
            aria-label="Export Excel"
          >
            <ExcelExportIcon
              style={{
                width: 18,
                height: 18,
              }}
            />
          </IconButton>
        </Box>
      </Box>

      {/* TABLE */}
      <Box
        sx={{
          bgcolor: "#2A3A4B",
          borderRadius: 2,
          overflowX: { xs: "auto", md: "visible" },
          WebkitOverflowScrolling: "touch",
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          "& table": {
            width: "max-content",
            minWidth: "100%",
            tableLayout: "auto !important",
          },
        }}
      >
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: "rgba(30,47,64,0.95)" }}>
              {/* Order Number */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <InventoryRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Order No.
                  </Typography>
                </Box>
              </TableCell>

              {/* Tag Number */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <CategoryRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Tag Nos
                  </Typography>
                </Box>
              </TableCell>

              {/* Image */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <LandscapeRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Image
                  </Typography>
                </Box>
              </TableCell>

              {/* Customer Name */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <PersonRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Customer Name
                  </Typography>
                </Box>
              </TableCell>

              {/* Customer Phone */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <LocalPhoneRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Phone
                  </Typography>
                </Box>
              </TableCell>

              {/* Ordered Date */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <CalendarMonthRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Order Date
                  </Typography>
                </Box>{" "}
              </TableCell>

              {/* Delivery Date */}
              <TableCell
                sx={{
                  color: "#fff",
                  fontWeight: 600,
                  fontSize: { xs: 13, sm: 15 },
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      borderRadius: "50%",
                      bgcolor: "#42A996",
                      p: 0.5,
                    }}
                  >
                    <EventAvailableRoundedIcon
                      sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                    />
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 600,
                      fontSize: { xs: 13, sm: 15 },
                    }}
                  >
                    Delivery Date
                  </Typography>
                </Box>
              </TableCell>

              {/* Reason - Only show for Reject(DM) status */}
              {orderStatus === "Reject(DM)" && (
                <TableCell
                  sx={{
                    color: "#fff",
                    fontWeight: 600,
                    fontSize: { xs: 13, sm: 15 },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        borderRadius: "50%",
                        bgcolor: "#42A996",
                        p: 0.5,
                      }}
                    >
                      <InfoRoundedIcon
                        sx={{ fontSize: { xs: 18, sm: 20 }, fill: "#FFF" }}
                      />
                    </Box>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: 13, sm: 15 },
                      }}
                    >
                      Reason
                    </Typography>
                  </Box>
                </TableCell>
              )}
            </TableRow>
          </TableHead>

          <TableBody>
            {orders.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={orderStatus === "Reject(DM)" ? 8 : 7}
                  sx={{ textAlign: "center", color: "#fff", py: 4 }}
                >
                  No orders found
                </TableCell>
              </TableRow>
            ) : (
              groupedOrders.map((order, index) => (
                <TableRow
                  key={order.id || index}
                  sx={{
                    bgcolor: index % 2 === 0 ? "#1E2F40" : "#2A3A4B",
                  }}
                >
                  {/* Order Number */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Typography
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: 13, sm: 15 },
                      }}
                    >
                      {order.order_no}
                    </Typography>
                  </TableCell>

                  {/* Tag Numbers */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {(Array.isArray(order.selectedTags)
                        ? order.selectedTags
                        : [order.selectedTags]
                      ).map((tag, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <Typography
                            sx={{
                              fontWeight: 600,
                              fontSize: { xs: 13, sm: 15 },
                            }}
                          >
                            {tag}
                          </Typography>
                        </Box>
                      ))}
                    </Box>
                  </TableCell>

                  {/* Image */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "flex-start",
                        flexDirection: "column",
                        gap: 1,
                      }}
                    >
                      {order?.selectedTagElements?.map((element, index) => (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                          }}
                        >
                          <SecureImage
                            item={element}
                            width={100}
                            height={100}
                            borderRadius={2}
                          />
                        </Box>
                      ))}
                    </Box>
                  </TableCell>

                  {/* Customer Name */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Typography sx={{ fontSize: { xs: 13, sm: 15 } }}>
                      {order.customerName || "-"}
                    </Typography>
                  </TableCell>

                  {/* Customer Phone */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Typography
                      component="a"
                      href={`tel:${order.customerPhone}`}
                      onClick={(e) => e.stopPropagation()}
                      sx={{
                        color: "#FFF",
                        textDecoration: "none",
                        fontSize: { xs: 13, sm: 15 },
                        cursor: "pointer",
                        transition: "all 0.3s ease",
                        "&:hover": {
                          color: "#4CAF50",
                          transform: "scale(1.05)",
                          textDecoration: "underline",
                        },
                      }}
                    >
                      {order.customerPhone || "-"}
                    </Typography>
                  </TableCell>

                  {/* Order Date */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Typography sx={{ fontSize: { xs: 13, sm: 15 } }}>
                      {formatDate(order.createdAt)}
                    </Typography>
                  </TableCell>

                  {/* Delivery Date */}
                  <TableCell sx={{ color: "#fff", py: 2 }}>
                    <Typography
                      sx={{
                        fontSize: { xs: 13, sm: 15 },
                        fontWeight: 600,
                      }}
                    >
                      {formatDate(order.deliveryDate)}
                    </Typography>
                  </TableCell>

                  {/* Reason - Only show for Reject(DM) status */}
                  {orderStatus === "Reject(DM)" && (
                    <TableCell sx={{ color: "#42A996", py: 2 }}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          flexDirection: "column",
                          gap: 1,
                        }}
                      >
                        {order.rejectedReasons?.length > 0 ? (
                          order.rejectedReasons
                            .filter((r) => r)
                            .map((reason, idx) => (
                              <Typography
                                key={idx}
                                sx={{ fontSize: { xs: 13, sm: 15 } }}
                              >
                                {reason}
                              </Typography>
                            ))
                        ) : (
                          <Typography sx={{ fontSize: { xs: 13, sm: 15 } }}>
                            -
                          </Typography>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Box>
    </Box>
  );
}

export default OrderStatusTable;
